'use client';

import { useEffect } from 'react';
import type { Property } from '../types/property';
import PriceAnalyticsPanel from './analytics/PriceAnalyticsPanel';

function formatPrice(price: number): string {
  return '£' + (price >= 1_000_000
    ? (price / 1_000_000).toFixed(price % 1_000_000 === 0 ? 0 : 1) + 'm'
    : price.toLocaleString('en-GB'));
}

function scoreMeta(score: number) {
  if (score >= 80) return { label: 'Strong match', color: 'text-emerald-600', bg: 'bg-emerald-50',  ring: 'ring-emerald-200' };
  if (score >= 60) return { label: 'Good match',   color: 'text-amber-600',   bg: 'bg-amber-50',    ring: 'ring-amber-200'   };
  return                 { label: 'Partial match', color: 'text-red-500',     bg: 'bg-red-50',      ring: 'ring-red-200'     };
}

// ── Stat pill ─────────────────────────────────────────────────────────────────
function StatPill({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-700">
      <span className="text-slate-400">{icon}</span>
      {label}
    </div>
  );
}

// SVG icons (avoids emoji inconsistency across platforms)
const Icons = {
  Check: () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  ),
  Close: () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  Bed: () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12V7a2 2 0 012-2h14a2 2 0 012 2v5M3 12h18M3 12v5m18-5v5M3 17h18" />
    </svg>
  ),
  Bath: () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 12h16v4a4 4 0 01-4 4H8a4 4 0 01-4-4v-4zM6 12V6a2 2 0 012-2h1" />
    </svg>
  ),
  Home: () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  ),
  Map: () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
};

// ── Modal ─────────────────────────────────────────────────────────────────────
interface Props {
  property: Property;
  onClose: () => void;
}

export default function PropertyModal({ property: p, onClose }: Props) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const { label: scoreLabel, color: scoreColor, bg: scoreBg, ring: scoreRing } = scoreMeta(p.matchScore);
  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-slate-900/50 text-white backdrop-blur-sm transition hover:bg-slate-900/70"
        >
          <Icons.Close />
        </button>

        {/* Hero */}
        <div className="relative h-60 w-full flex-shrink-0 overflow-hidden bg-slate-100">
          {p.imageUrl ? (
            <img src={p.imageUrl} alt={p.title} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-5xl text-slate-300">🏠</div>
          )}
          {/* subtle bottom scrim so content feels grounded */}
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/30 to-transparent" />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">

          {/* Price + score */}
          <div className="flex items-start justify-between gap-4 px-6 pt-5">
            <div>
              <p className="text-3xl font-extrabold tracking-tight text-slate-900">{formatPrice(p.price)}</p>
              <h2 className="mt-1 text-lg font-semibold text-slate-700">{p.title}</h2>
              <p className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-400">
                <Icons.Map />
                {p.address}, {p.postcode}
              </p>
            </div>
            <div className={`${scoreBg} ${scoreRing} ring-1 flex flex-shrink-0 flex-col items-center justify-center rounded-2xl px-4 py-3 text-center`}>
              <span className={`${scoreColor} text-3xl font-extrabold leading-none`}>{p.matchScore}</span>
              <span className={`${scoreColor} mt-1 text-[11px] font-semibold`}>{scoreLabel}</span>
            </div>
          </div>

          {/* Stat pills */}
          <div className="mt-4 flex flex-wrap gap-2 px-6">
            <StatPill icon={<Icons.Bed />}  label={`${p.bedrooms} bedroom${p.bedrooms !== 1 ? 's' : ''}`} />
            <StatPill icon={<Icons.Bath />} label={`${p.bathrooms} bathroom${p.bathrooms !== 1 ? 's' : ''}`} />
            <StatPill icon={<Icons.Home />} label={capitalize(p.propertyType)} />
          </div>

          {/* Divider */}
          <hr className="mx-6 my-5 border-slate-100" />

          {/* Description */}
          <div className="px-6">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-slate-400">About this property</p>
            <p className="text-sm leading-relaxed text-slate-600">{p.description}</p>
          </div>

          {/* Why it matches */}
          <div className="mt-5 px-6">
            <div className="mb-2 flex items-center gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Why it matches your search</p>
              <span className="rounded-md bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold text-blue-600">AI</span>
            </div>
            {p.matchingCriteria && p.matchingCriteria.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {p.matchingCriteria.map((c) => (
                  <span key={c} className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-[13px] font-medium text-emerald-700 ring-1 ring-emerald-200">
                    <Icons.Check />
                    {c}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm italic text-slate-400">Submit a search brief to see how this property matches your criteria.</p>
            )}
          </div>

          {/* Area intelligence — Price Analytics / Neighbourhood / Ask AI */}
          <div className="mt-5 px-6 pb-6">
            <div className="mb-3 flex items-center gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Area intelligence</p>
              <span className="rounded-md bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold text-violet-600">PropertyData API</span>
            </div>
            <PriceAnalyticsPanel
              postcode={p.postcode}
              propertyType={p.propertyType}
              askingPrice={p.price}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
